import {produce} from 'immer';
import i18next from 'i18next';

import {type SelectOption} from '@famly/mf_input_select';
import {isNonEmpty, head} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';
import {isEmpty} from 'web-app/util/array';
import {toRecord} from 'web-app/util/array';
import {toPartialFunction} from 'web-app/util/record';
import * as Warn from 'web-app/util/warnable';

import * as DateFormatter from './helpers/date-formatter';
import {
    allSelectedChildrenHaveBillPayer,
    allSelectedTuitionsHaveTheSameFrequency,
    isAutomaticPricingGroup,
    periodPricingGroups,
    selectedChildren,
} from './helpers/helpers';
import {secondBillingPeriodByBillingType} from './helpers/second-billing-period';
import type * as Types from './types';
import {AUTOMATIC_PRICING_GROUP_ID, BILLING_FREQUENCY_OPTIONS} from './constants';
import {getChildrenMissingBirthday} from './helpers/children-errors';
import {getTuitionBillingFrequency} from './transformers/plan-transformers';
import {isCreateSingle} from './helpers/state-projections';

const EDIT_VIEW_ = 'EDIT_VIEW_';

const reducer: Types.Reducer = produce((state, action) => {
    switch (action.type) {
        case 'SELECT_CHILDREN': {
            const updatedChildrenSelection = selectedChildren(
                state.childrenState.allChildren,
                action.payload.selectedChildren,
            );

            updateChildrenErrors({state, updatedChildrenSelection});

            state.childrenState.selectedChildren = updatedChildrenSelection;

            state.childrenState.allSelectedChildrenHaveBillPayer = allSelectedChildrenHaveBillPayer(
                state.childrenState.selectedChildren,
            );

            const {secondMonthlyPeriod, secondWeeklyPeriod} = secondBillingPeriodByBillingType({
                monthlyBillingPeriods: state.billingPeriodState.monthly,
                weeklyBillingPeriods: state.billingPeriodState.weekly,
            });

            if (state.billingPeriodState.selectedBillingPeriod) {
                if ('month' in state.billingPeriodState.selectedBillingPeriod) {
                    state.pricingGroupState.monthlyPeriodPricingGroups = periodPricingGroups({
                        periodDateRange: state.billingPeriodState.selectedBillingPeriod,
                        selectedChildren: state.childrenState.selectedChildren,
                    });
                }
                if (state.billingPeriodState.selectedBillingPeriod.type !== 'custom') {
                    state.pricingGroupState.weeklyPeriodPricingGroups = periodPricingGroups({
                        periodDateRange: state.billingPeriodState.selectedBillingPeriod,
                        selectedChildren: state.childrenState.selectedChildren,
                    });
                }
            } else {
                if (secondMonthlyPeriod) {
                    state.pricingGroupState.monthlyPeriodPricingGroups = periodPricingGroups({
                        periodDateRange: secondMonthlyPeriod,
                        selectedChildren: state.childrenState.selectedChildren,
                    });
                }
                if (secondWeeklyPeriod) {
                    state.pricingGroupState.weeklyPeriodPricingGroups = periodPricingGroups({
                        periodDateRange: secondWeeklyPeriod,
                        selectedChildren: state.childrenState.selectedChildren,
                    });
                }
            }

            break;
        }
        case 'INITIALISE_STATE': {
            const {tuitions, fees, discounts, pricingGroupState, biweeklyBillingPeriods} = action.payload;

            state.tuitionsState.allTuitions = tuitions;
            state.feesState.allFees = fees;
            state.discountState.allDiscounts = discounts;
            state.pricingGroupState = pricingGroupState;
            state.billingPeriodState.biweekly = biweeklyBillingPeriods;

            break;
        }
        case 'SELECT_TUITION': {
            const {index, selectedOption, billingFrequencyOptions, availableBillingFrequencies} = action.payload;

            const selectedTuition = state.tuitionsState.allTuitions.find(
                tuition => tuition.id === selectedOption.value,
            );

            if (!selectedTuition) {
                break;
            }

            state.tuitionsState.selectedTuitions[index] = selectedTuition;
            state.tuitionsState.inputFields[index].id = selectedTuition.id;
            state.selectedBillingType = selectedTuition.billingProfile.type;

            if (state.tuitionsState.selectedTuitions.length === 1) {
                state.feesState.selectedFees = [];
                state.feesState.inputFields.map(field => {
                    field.id = '';
                });
            }

            if (!isNonEmpty(state.tuitionsState.selectedTuitions)) {
                state.tuitionsState.allSelectedTuitionsHaveTheSameFrequency = true;
                break;
            }

            state.tuitionsState.allSelectedTuitionsHaveTheSameFrequency = allSelectedTuitionsHaveTheSameFrequency(
                state.tuitionsState.selectedTuitions,
            );

            if (state.view === 'edit') {
                break;
            }

            const billingFrequencyOption = billingFrequencyOptions.find(option => option.type === selectedTuition.type);
            if (billingFrequencyOption) {
                selectBillingFrequency({
                    state,
                    payload: {selectedOption: billingFrequencyOption, availableBillingFrequencies},
                });
            }

            break;
        }
        case 'ADD_NEW_TUITION_FIELD': {
            state.tuitionsState.inputFields = [...state.tuitionsState.inputFields, action.payload];

            break;
        }
        case 'REMOVE_TUITION_FIELD': {
            state.tuitionsState.inputFields = state.tuitionsState.inputFields.filter(
                (_, index) => index !== action.payload.index,
            );
            state.tuitionsState.selectedTuitions = state.tuitionsState.selectedTuitions.filter(
                (_, index) => index !== action.payload.index,
            );

            if (!isNonEmpty(state.tuitionsState.selectedTuitions)) {
                state.tuitionsState.allSelectedTuitionsHaveTheSameFrequency = true;

                if (
                    hasValue(state.billingFrequencyState.selectedBillingFrequency) &&
                    state.billingFrequencyState.selectedBillingFrequency.type !== 'none' &&
                    state.billingFrequencyState.selectedBillingFrequency.type !== 'biweekly'
                ) {
                    state.selectedBillingType = state.billingFrequencyState.selectedBillingFrequency.type;
                }

                return;
            }

            state.tuitionsState.allSelectedTuitionsHaveTheSameFrequency = allSelectedTuitionsHaveTheSameFrequency(
                state.tuitionsState.selectedTuitions,
            );

            break;
        }
        case 'SELECT_BILLING_PERIOD': {
            const {selectedOption} = action.payload;
            if (!selectedOption) {
                state.billingPeriodState.selectedBillingPeriod = null;
                return;
            }

            if (selectedOption?.type === 'custom') {
                state.billingPeriodState.selectedBillingPeriod = {
                    type: 'custom',
                    startDate: state.customPeriodState.customPeriodDates
                        ? {
                              numericFormat: DateFormatter.numericFormat(
                                  state.customPeriodState.customPeriodDates.startDate,
                              ),
                              textFormat: DateFormatter.textFormat(state.customPeriodState.customPeriodDates.startDate),
                          }
                        : undefined,
                    endDate: state.customPeriodState.customPeriodDates
                        ? {
                              numericFormat: DateFormatter.numericFormat(
                                  state.customPeriodState.customPeriodDates.endDate,
                              ),
                              textFormat: DateFormatter.textFormat(state.customPeriodState.customPeriodDates.endDate),
                          }
                        : undefined,
                    unformattedStartDate: state.customPeriodState.customPeriodDates?.startDate,
                    unformattedEndDate: state.customPeriodState.customPeriodDates?.endDate,
                };
                return;
            }

            if (!state.selectedBillingType) {
                return;
            }

            if (selectedOption.type === 'biweekly') {
                const selectedBillingPeriod = state.billingPeriodState.biweekly?.periods.find(period => {
                    return period.startDate?.numericFormat === selectedOption?.value;
                });

                updateSelectedBillingPeriod({state, selectedBillingPeriod});
                return;
            }

            const selectedBillingPeriod = state.billingPeriodState[state.selectedBillingType]?.periods.find(period => {
                return period.startDate?.numericFormat === selectedOption?.value;
            }) as Types.Month | Types.Week | Types.CustomPeriod | undefined;

            updateSelectedBillingPeriod({state, selectedBillingPeriod});
            break;
        }
        case 'SELECT_BILLING_FREQUENCY': {
            selectBillingFrequency({state, payload: action.payload});
            break;
        }
        case 'UPDATE_CUSTOM_PERIOD_MODAL_STATE': {
            const {customPeriodDates} = action.payload;
            state.customPeriodState.customPeriodDates = customPeriodDates;
            break;
        }
        case 'UPDATE_PRORATED_ITEM': {
            const {id, title, price} = action.payload;
            const tuitionOrFeeToUpdate =
                state.tuitionsState.selectedTuitions.find(tuition => tuition.id === id) ??
                state.feesState.selectedFees.find(fee => fee.id === id);

            if (!tuitionOrFeeToUpdate) {
                return;
            }
            tuitionOrFeeToUpdate.customStartPeriod = {title, price};
            return;
        }

        case 'SELECT_FEE': {
            const {index, selectedOption} = action.payload;

            const selectedFee = state.feesState.allFees.find(fee => fee.id === selectedOption.value);

            if (!selectedFee) {
                return;
            }

            state.feesState.selectedFees[index] = selectedFee;
            state.feesState.inputFields[index].id = selectedFee.id;

            break;
        }
        case 'ADD_NEW_FEE_FIELD': {
            state.feesState.inputFields = [...state.feesState.inputFields, action.payload];

            break;
        }

        case 'REMOVE_FEE_FIELD': {
            state.feesState.selectedFees = state.feesState.selectedFees.filter(
                (_, index) => index !== action.payload.index,
            );

            state.feesState.inputFields = state.feesState.inputFields.filter(
                (_, index) => index !== action.payload.index,
            );

            break;
        }

        case 'TOGGLE_MORE_OPTIONS': {
            state.showMoreOptions = action.payload;
            break;
        }
        case 'SELECT_PRICING_GROUP': {
            const {selectedOption, allPricingGroups} = action.payload;
            state.pricingGroupState.selectedPricingGroup = selectedOption;

            const isAutomaticPricingGroupSelected = selectedOption === AUTOMATIC_PRICING_GROUP_ID;

            if (isAutomaticPricingGroupSelected) {
                const tuitionsWithoutAllPricingGroups = filterItemsWithoutAllPricingGroups(
                    state.tuitionsState.selectedTuitions,
                    allPricingGroups,
                );
                const feesWithoutAllPricingGroups = filterItemsWithoutAllPricingGroups(
                    state.feesState.selectedFees,
                    allPricingGroups,
                );

                const compatibleTuitions = state.tuitionsState.selectedTuitions.filter(
                    selectedTuition => !tuitionsWithoutAllPricingGroups.includes(selectedTuition),
                );
                const compatibleFees = state.feesState.selectedFees.filter(
                    selectedFee => !feesWithoutAllPricingGroups.includes(selectedFee),
                );

                state.tuitionsState.selectedTuitions = compatibleTuitions;
                state.tuitionsState.inputFields = isNonEmpty(compatibleTuitions)
                    ? compatibleTuitions.map(tuition => ({id: tuition.id}))
                    : [{id: ''}];

                state.feesState.selectedFees = compatibleFees;
                state.feesState.inputFields = compatibleFees.map(fee => ({
                    id: fee.id,
                }));
            } else {
                const compatibleTuitions = state.tuitionsState.selectedTuitions.filter(selectedTuition =>
                    selectedTuition.pricingGroups.some(pricingGroup => pricingGroup.id === selectedOption),
                );
                const compatibleFees = state.feesState.selectedFees.filter(selectedFee =>
                    selectedFee.pricingGroups.some(pricingGroup => pricingGroup.id === selectedOption),
                );

                state.tuitionsState.selectedTuitions = compatibleTuitions;
                state.tuitionsState.inputFields = isNonEmpty(compatibleTuitions)
                    ? compatibleTuitions.map(tuition => ({id: tuition.id}))
                    : [{id: ''}];

                state.feesState.selectedFees = compatibleFees;
                state.feesState.inputFields = compatibleFees.map(fee => ({
                    id: fee.id,
                }));
            }

            if (state.view === 'create' && state.tuitionsState.selectedTuitions.length === 0) {
                state.billingFrequencyState.selectedBillingFrequency = null;
                state.billingPeriodState.selectedBillingPeriod = null;
            }
            break;
        }
        case 'SET_DISCOUNTS': {
            const {selectedOptions} = action.payload;

            state.discountState.selectedDiscounts = selectedOptions
                .map(selectedOptionId =>
                    state.discountState.allDiscounts.find(discount => discount.id === selectedOptionId),
                )
                .filter(hasValue);

            break;
        }
        case 'SELECT_END_DATE': {
            if (action.payload.type === 'error') {
                state.endDateState.error = action.payload.message;
                break;
            }
            state.endDateState.selectedEndDate = action.payload.endDate;
            break;
        }
        case 'UPDATE_END_PERIOD_PRORATED_ITEM': {
            const {id, title, price} = action.payload;
            const tuitionOrFeeToUpdate =
                state.tuitionsState.selectedTuitions.find(tuition => tuition.id === id) ??
                state.feesState.selectedFees.find(fee => fee.id === id);

            if (!tuitionOrFeeToUpdate) {
                return;
            }
            tuitionOrFeeToUpdate.customEndPeriod = {title, price};
            break;
        }
        case 'CANCEL_CUSTOM_END_PERIOD': {
            state.displayedModal.type = 'none';
            state.endDateState.selectedEndDate = null;
            state.tuitionsState.selectedTuitions = state.tuitionsState.selectedTuitions.map(tuition => {
                tuition.customEndPeriod = undefined;
                return tuition;
            });
            state.feesState.selectedFees = state.feesState.selectedFees.map(fee => {
                fee.customEndPeriod = undefined;
                return fee;
            });
            break;
        }
        case 'TOGGLE_TUITION_FIELD': {
            state.tuitionsState.fieldCurrentlyOpen = action.payload.maybeIndex;
            break;
        }
        case 'TOGGLE_FEE_FIELD': {
            state.feesState.fieldCurrentlyOpen = action.payload.maybeIndex;
            break;
        }
        case 'TOGGLE_MODAL': {
            if (action.payload.type === 'addDiscount') {
                if (!hasValue(state.selectedBillingType)) {
                    state.displayedModal = {type: 'error', errorMessage: i18next.t('somethingWentWrong')};
                    break;
                }
                state.displayedModal = {...action.payload, billingType: state.selectedBillingType};
                break;
            }

            if (action.payload.type === 'addFee') {
                if (!hasValue(state.selectedBillingType)) {
                    state.displayedModal = {type: 'error', errorMessage: i18next.t('somethingWentWrong')};
                    break;
                }
                state.displayedModal = {
                    ...action.payload,
                    payload: {
                        ...action.payload.payload,
                        billingType: state.selectedBillingType,
                    },
                };
                break;
            }

            if (action.payload.type === 'overrideTuitionPrice') {
                if (!hasValue(state.pricingGroupState.selectedPricingGroup)) {
                    state.displayedModal = {type: 'error', errorMessage: i18next.t('somethingWentWrong')};
                    break;
                }
                state.displayedModal = {
                    type: 'overrideTuitionPrice',
                    payload: {
                        tuition: action.payload.payload.tuition,
                        selectedPricingGroup: state.pricingGroupState.selectedPricingGroup,
                    },
                };
                break;
            }

            if (action.payload.type === 'addTuition') {
                state.displayedModal = action.payload;
                break;
            }
            if (action.payload.type === 'endPeriod') {
                state.endDateState.error = null;
            }

            state.displayedModal.type = action.payload.type;
            break;
        }
        case 'UPDATE_ALL_TUITIONS': {
            state.tuitionsState.allTuitions = [...state.tuitionsState.allTuitions, action.payload.newTuition];
            break;
        }
        case 'UPDATE_ALL_FEES': {
            state.feesState.allFees = [...state.feesState.allFees, action.payload.newFee];
            break;
        }
        case 'UPDATE_ALL_DISCOUNTS': {
            state.discountState.allDiscounts = [...state.discountState.allDiscounts, action.payload.newDiscount];
            break;
        }
        case 'SAVE_TUITION_PRICE_OVERRIDE': {
            const {id, price} = action.payload;
            state.tuitionsState.selectedTuitions = state.tuitionsState.selectedTuitions.map(tuition => {
                if (tuition.id === id) {
                    tuition.overriddenPrice = price;
                }
                return tuition;
            });
            break;
        }
        case 'SET_EMPTY_PLAN_DATE_RANGE': {
            state.emptyPlan.startDate = action.payload.startDate;
            state.emptyPlan.endDate = action.payload.endDate;
            break;
        }
        case 'INITIALISE_PLAN_OR_TEMPLATE': {
            const {plan} = action.payload;

            initialisePlanOrTemplate({state, plan});

            if (state.billingFrequencyState.selectedBillingFrequency?.type === 'biweekly') {
                selectDefaultBillingPeriod({state, period: 'biweekly'});
                return;
            }

            if (state.selectedBillingType) {
                selectDefaultBillingPeriod({state, period: state.selectedBillingType});
            }
            break;
        }
        case 'TOGGLE_ADD_NEW_TEMPLATE': {
            state.template.shouldAddNewTemplate = action.payload.shouldAddNewTemplate;
            break;
        }
        case 'SET_NEW_TEMPLATE_NAME': {
            state.template.newTemplateName = action.payload.newTemplateName;
            break;
        }
        case 'UPDATE_PLAN_VALIDATION_ERRORS': {
            // The filtering has to be done in 2 steps otherwise the type inference is not working properly
            state.childrenErrors = action.payload.results
                .filter(result => result.type !== 'success')
                .filter(error => error.type !== 'server-error');
            break;
        }
        case `${EDIT_VIEW_}UPDATE_PLAN_START_DATE`: {
            const hasFullPlanBilledError = state.childrenErrors.some(error => error.type === 'full-plan-billed');
            if (state.view === 'edit' && !hasFullPlanBilledError) {
                state.planDateRange = {
                    startDate: action.payload.startDate,
                    endDate: state.planDateRange?.endDate ?? null,
                };
            }
            break;
        }
        case `${EDIT_VIEW_}UPDATE_PLAN_END_DATE`: {
            const hasFullPlanBilledError = state.childrenErrors.some(error => error.type === 'full-plan-billed');
            if (state.view === 'edit' && !hasFullPlanBilledError && hasValue(state.planDateRange)) {
                state.planDateRange = {
                    startDate: state.planDateRange.startDate,
                    endDate: action.payload.endDate,
                };
            }
            break;
        }
        case 'REMOVE_CHILD_WITH_ERROR': {
            const childError = action.payload.childError;
            removeChildWithError(state, childError);
            break;
        }
        default:
            exhaustiveCheck(action);
            break;
    }
});

export const initialisePlanOrTemplate = ({state, plan}: {state: Types.State; plan: Types.Plan | Types.Template}) => {
    if (state.view === 'edit') {
        state.selectedPlanId = plan.id;
    }
    state.pricingGroupState.selectedPricingGroup =
        plan.pricing.type === 'automatic' ? AUTOMATIC_PRICING_GROUP_ID : plan.pricing.pricingGroup;

    const hasOnlyValidTuitions = plan.tuitions.every(planTuition =>
        state.tuitionsState.allTuitions.some(tuition => tuition.id === planTuition.id),
    );

    if (isNonEmpty(plan.tuitions) && hasOnlyValidTuitions) {
        state.tuitionsState.selectedTuitions = state.tuitionsState.allTuitions.filter(tuition =>
            plan.tuitions.some(planTuition => planTuition.id === tuition.id),
        );
        state.tuitionsState.inputFields = state.tuitionsState.selectedTuitions.map(tuition => ({id: tuition.id}));
    }

    state.feesState.selectedFees = state.feesState.allFees.filter(fee => plan.fees.includes(fee.id));
    state.feesState.inputFields = state.feesState.selectedFees.map(fee => ({id: fee.id}));

    // Only set the billing frequency from the plan in create mode or if it couldn't be determined from the bill payer in edit mode.
    if (state.view === 'create' || Warn.isWarnings(state.billingFrequencyState.initialBillingFrequency)) {
        state.billingFrequencyState.initialBillingFrequency = (() => {
            if (hasValue(plan.billingFrequency)) {
                return Warn.resolve(plan.billingFrequency);
            }
            const getTuitionPeriodById: (id: string) => Types.BillingType | undefined = toPartialFunction(
                toRecord(
                    state.tuitionsState.allTuitions,
                    tuition => tuition.id,
                    tuition => tuition.type,
                ),
            );
            return Warn.bind(getTuitionBillingFrequency(plan.tuitions, getTuitionPeriodById), billingFrequency =>
                hasValue(billingFrequency)
                    ? Warn.resolve(billingFrequency)
                    : Warn.warningMessage('monthly', 'Falling back to monthly billing frequency.'),
            );
        })();
    }

    state.billingFrequencyState.selectedBillingFrequency =
        BILLING_FREQUENCY_OPTIONS.find(
            option => option.type === state.billingFrequencyState.initialBillingFrequency.value,
        ) ?? null;

    state.discountState.selectedDiscounts = state.discountState.allDiscounts.filter(discount =>
        plan.discounts.includes(discount.id),
    );

    state.selectedBillingType = state.tuitionsState.selectedTuitions[0]?.type ?? null;
};

const selectBillingFrequency = ({
    state,
    payload: {selectedOption, availableBillingFrequencies},
}: {
    state: Types.State;
    payload: Types.SelectBillingFrequencyPayload;
}) => {
    state.billingFrequencyState.selectedBillingFrequency = selectedOption;

    if (selectedOption.type === 'none') {
        state.billingFrequencyState.billingFrequencyTypeToBeSetUp = null;

        state.selectedBillingType = isNonEmpty(state.tuitionsState.selectedTuitions)
            ? head(state.tuitionsState.selectedTuitions).billingProfile.type
            : null;

        if (isNonEmpty(state.tuitionsState.selectedTuitions)) {
            if (state.tuitionsState.allSelectedTuitionsHaveTheSameFrequency) {
                selectDefaultBillingPeriod({
                    state,
                    period: head(state.tuitionsState.selectedTuitions).billingProfile.type,
                });
            } else {
                state.billingPeriodState.selectedBillingPeriod = null;
            }
        }

        return;
    }

    state.selectedBillingType = selectedOption.type === 'biweekly' ? 'weekly' : selectedOption.type;

    selectDefaultBillingPeriod({state, period: selectedOption.type});

    const alreadyHasSetUpAtLeastOneBillingFrequency = isNonEmpty(availableBillingFrequencies);
    if (alreadyHasSetUpAtLeastOneBillingFrequency) {
        const isSelectedBillingFrequencyAlreadySetUp = hasValue(
            availableBillingFrequencies.find(frequency => frequency.type === selectedOption.type),
        );
        if (isSelectedBillingFrequencyAlreadySetUp) {
            state.billingFrequencyState.billingFrequencyTypeToBeSetUp = null;
            return;
        }

        switch (selectedOption.type) {
            case 'monthly': {
                state.billingFrequencyState.billingFrequencyTypeToBeSetUp = 'monthly-partial';
                break;
            }
            case 'weekly': {
                state.billingFrequencyState.billingFrequencyTypeToBeSetUp = 'weekly-partial';
                break;
            }
            case 'biweekly': {
                state.billingFrequencyState.billingFrequencyTypeToBeSetUp = 'biweekly-partial';
                break;
            }
            default:
                state.billingFrequencyState.billingFrequencyTypeToBeSetUp = null;
        }
        return;
    }

    state.billingFrequencyState.billingFrequencyTypeToBeSetUp = selectedOption.type;
    return;
};

export const updateSelectedBillingPeriod = ({
    state,
    selectedBillingPeriod,
}: {
    state: Types.State;
    selectedBillingPeriod: undefined | Types.BillingPeriods['periods'][number];
}) => {
    state.billingPeriodState.selectedBillingPeriod = selectedBillingPeriod ?? null;

    if (hasValue(state.customPeriodState.customPeriodDates)) {
        state.customPeriodState.customPeriodDates = null;
    }

    if (selectedBillingPeriod) {
        if ('month' in selectedBillingPeriod) {
            state.pricingGroupState.monthlyPeriodPricingGroups = periodPricingGroups({
                periodDateRange: selectedBillingPeriod,
                selectedChildren: state.childrenState.selectedChildren,
            });
            return;
        }
        if (selectedBillingPeriod.type !== 'custom') {
            state.pricingGroupState.weeklyPeriodPricingGroups = periodPricingGroups({
                periodDateRange: selectedBillingPeriod,
                selectedChildren: state.childrenState.selectedChildren,
            });
        }
    }
};

export const selectDefaultBillingPeriod = ({
    state,
    period,
}: {
    state: Types.State;
    period: Extract<keyof Types.State['billingPeriodState'], 'weekly' | 'monthly' | 'biweekly'>;
}) => {
    const indexOfPeriodToSelect = isCreateSingle(state) ? 0 : 1;
    const defaultBillingPeriod = state.billingPeriodState[period]?.periods[indexOfPeriodToSelect];
    updateSelectedBillingPeriod({state, selectedBillingPeriod: defaultBillingPeriod});
};

export const removeChildWithError = (state: Types.State, childError: Types.ChildrenError) => {
    if (childError.type === 'server-error') {
        return;
    }
    state.childrenState.selectedChildren = state.childrenState.selectedChildren.filter(
        child => !childError.childIds.includes(child.id),
    );

    // Remove the error from the state and remove the child from the childIds list of other errors
    // if the error has no more child ids, remove it from the state
    state.childrenErrors = state.childrenErrors
        .filter(error => error !== childError)
        .map(error => removeChildrenFromError(error, childError.childIds))
        .filter(hasValue);
};

const removeChildrenFromError = <T extends Types.ChildrenError>(
    childrenError: T,
    childIds: Array<Types.Child['id']>,
): T | null => {
    if (childrenError.type !== 'server-error') {
        childrenError.childIds = childrenError.childIds.filter(id => !childIds.includes(id));
        if (isEmpty(childrenError.childIds)) {
            return null;
        }
    }
    return childrenError;
};

export const updateChildrenErrors = ({
    state,
    updatedChildrenSelection,
}: {
    state: Types.State;
    updatedChildrenSelection: Array<Types.Child>;
}) => {
    const removedChildrenIds = state.childrenState.selectedChildren
        .filter(child => !updatedChildrenSelection.some(updatedChild => updatedChild.id === child.id))
        .map(child => child.id);

    state.childrenErrors = state.childrenErrors
        .map(error => removeChildrenFromError(error, removedChildrenIds))
        .filter(hasValue);

    const childrenMissingBirthday = getChildrenMissingBirthday(updatedChildrenSelection);
    if (childrenMissingBirthday.length > 0) {
        const missingBirthdayError = state.childrenErrors.find(error => error.type === 'missing-birthday');
        if (missingBirthdayError) {
            state.childrenErrors = state.childrenErrors.map(error => {
                if (error.type === 'missing-birthday') {
                    return {
                        ...error,
                        childIds: childrenMissingBirthday.map(child => child.id),
                    };
                }
                return error;
            });
        } else {
            state.childrenErrors = [
                {
                    type: 'missing-birthday',
                    childIds: childrenMissingBirthday.map(child => child.id),
                    message: i18next.t(
                        'react.simplifiedUsBillingPlan.translations.json.childrenErrors.missingBirthday',
                    ),
                },
                ...state.childrenErrors,
            ];
        }
    }
};

const filterItemsWithoutAllPricingGroups = <T extends Types.Tuition | Types.Fee>(
    items: T[],
    allPricingGroups: Array<SelectOption>,
): T[] => {
    const allPricingGroupsWithoutAutomatic = allPricingGroups.filter(
        pricingGroup => !isAutomaticPricingGroup(pricingGroup),
    );

    return items.filter(item => {
        const itemPricingGroupIds = item.pricingGroups.map(pricingGroup => pricingGroup.id);
        return allPricingGroupsWithoutAutomatic.some(pricingGroup => !itemPricingGroupIds.includes(pricingGroup.value));
    });
};

export {reducer};
